import { MouseEvent, memo, useCallback, useMemo, useState } from 'react';
import { useTranslator } from '@picsart/rc/services/localization';
import { batch, useDispatch } from 'react-redux';
import { TabPanel } from '@picsart/tabs';
import View from '@picsart/view/View';
import Spacings from '@picsart/cascade/spacings';
import { Button, ButtonSizeXL, ButtonSkinStatusNegative, ButtonVariants } from '@picsart/button';
import { Segment, Segments, SegmentsSizesSM } from '@picsart/segments';
import { ExpandStates } from '@picsart/editorlayout/types';
import Dynamic from '@picsart/editorlayout/Dynamic';
import DynamicHeader from '@picsart/editorlayout/DynamicHeader';
import DynamicBody from '@picsart/editorlayout/DynamicBody';

import useMount from 'hooks/common/useMount';
import batchSlice from 'store/batch';
import initialState, { initCropTemplateValue } from 'store/batch/initialState';
import useMemoSelector from 'hooks/useMemoSelector';
import useUnMount from 'hooks/common/useUnMount';
import { getCropFlip, getCropRotate, getCropTemplate } from 'store/batch/selectors';
import { ICropTemplateValue } from 'pages/Batch/types';
import analytics, { SubToolNames } from 'pages/Batch/utils/analytics';
import { BATCH_TOOLS, cropOrientationValues } from 'pages/Batch/configs';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import { CropTabs } from 'configs/constants';
import RatioCards, { PartialCard } from 'components/common/RatioCards';
import { getShowAsCategoryCardData } from 'store/selectors/general';
import useHeaderStyles from 'pages/Batch/styles';
import dynamicTestId from 'utils/common/dynamicTestId';

import { IProcessingTool } from '../types';

import useStyles from './styles';
import { RotationOptionEnum } from './types';
import testIds from './testIds';
import { MAX_ROTATE, ROTATE_OPTIONS, ROTATE_STEP } from './constants';

const CropTool = ({ inProgress }: IProcessingTool) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const headerClasses = useHeaderStyles();
  const translate = useTranslator();
  const { selectedCard, showAsCategoryCardData, cropRotate, cropFlip } = useMemoSelector(state => ({
    showAsCategoryCardData: getShowAsCategoryCardData(state),
    selectedCard: getCropTemplate(state),
    cropRotate: getCropRotate(state),
    cropFlip: getCropFlip(state),
  }));
  const { expandedState, isWeb } = useBatchData();
  const [activeTab, setActiveTab] = useState<CropTabs>(CropTabs.crop);

  const templateData = useMemo<PartialCard[]>(() => showAsCategoryCardData.map((item, i) => (i === 0
    ? {
      ...item,
      name: translate('Original'),
      iconName: 'all-templates-editor',
    } : {
      ...item,
      name: translate(item.name),
      iconUrl: item.preview_small_url,
      sizes: item.size,
    })), [showAsCategoryCardData, translate]);

  const handleCardSelect = useCallback((value: PartialCard) => {
    const { height, width } = value.size;
    const cropOrientation = height >= width ? cropOrientationValues[0] : cropOrientationValues[1];

    batch(() => {
      // TODO:miniapp unify all cards related types
      dispatch(batchSlice.actions.setCropTemplate(value as ICropTemplateValue));
      dispatch(batchSlice.actions.setCropAlign(null));
      dispatch(batchSlice.actions.setCropOrientation(cropOrientation));
    });
    analytics.utils().setSubTool(BATCH_TOOLS.crop, {
      name: SubToolNames.crop,
      value: value.title as string,
    });
  }, [dispatch]);

  const handleTabChange = useCallback((ev: MouseEvent<HTMLElement>) => {
    const { tab } = ev.currentTarget.dataset;
    setActiveTab(Number(tab));
  }, []);

  const handleRotate = useCallback((tool: RotationOptionEnum) => {
    if ([RotationOptionEnum.rotateLeft, RotationOptionEnum.rotateRight].includes(tool)) {
      let deg = 0;
      if (tool === RotationOptionEnum.rotateLeft) {
        deg = -ROTATE_STEP;
      } else if (tool === RotationOptionEnum.rotateRight) {
        deg = ROTATE_STEP;
      }

      let rotateValue = cropRotate + deg;
      if (rotateValue === MAX_ROTATE) rotateValue = 0;
      if (rotateValue < 0) rotateValue = MAX_ROTATE + rotateValue;

      dispatch(batchSlice.actions.setCropRotate(rotateValue));
    } else if ([RotationOptionEnum.flipVertical, RotationOptionEnum.flipHorizontal].includes(tool)) {
      const flip = {
        x: cropFlip.x,
        y: cropFlip.y,
      };

      const isPortrait = [0, 180].includes(cropRotate);

      if (tool === RotationOptionEnum.flipHorizontal) {
        flip[isPortrait ? 'x' : 'y'] = (cropFlip[isPortrait ? 'x' : 'y'] === -1 ? 1 : -1);
      }

      if (tool === RotationOptionEnum.flipVertical) {
        flip[isPortrait ? 'y' : 'x'] = (cropFlip[isPortrait ? 'y' : 'x'] === -1 ? 1 : -1);
      }

      dispatch(batchSlice.actions.setCropFlip(flip));
    }
    analytics.utils().setSubTool(BATCH_TOOLS.crop, {
      name: SubToolNames.rotate,
      value: tool,
    });
  }, [dispatch, cropRotate, cropFlip]);

  useMount(() => {
    analytics.openEvent(BATCH_TOOLS.crop);
  });

  useUnMount(() => {
    batch(() => {
      dispatch(batchSlice.actions.setCropTemplate(initCropTemplateValue));
      dispatch(batchSlice.actions.setCropAlign(null));
      dispatch(batchSlice.actions.setCropOrientation(cropOrientationValues[0]));
      dispatch(batchSlice.actions.setCropRotate(initialState.cropRotate));
      dispatch(batchSlice.actions.setCropFlip(initialState.cropFlip));
    });
  });

  return (
    <Dynamic
      header={(
        <DynamicHeader
          className={headerClasses.customHeader}
          title={translate('Crop')}
        >
          <Segments size={SegmentsSizesSM} data-testid={testIds.tabs}>
            <Segment
              isActive={activeTab === CropTabs.crop}
              data-testid={testIds.cropBtn}
              data-tab={CropTabs.crop}
              onClick={handleTabChange}
            >
              {translate('Crop')}
            </Segment>
            <Segment
              isActive={activeTab === CropTabs.rotate}
              data-testid={testIds.rotateBtn}
              data-tab={CropTabs.rotate}
              onClick={handleTabChange}
            >
              {translate('Rotate')}
            </Segment>
          </Segments>
        </DynamicHeader>
      )}
      body={(
        <DynamicBody>
          <View marginBlock={Spacings.s16}>
            <TabPanel className={classes.fullWidth} index={CropTabs.crop} value={activeTab}>
              <RatioCards showAsCarousel={!isWeb && expandedState !== ExpandStates.Full} onCardSelect={handleCardSelect} selectedCard={selectedCard as PartialCard} cards={templateData} />
            </TabPanel>

            <TabPanel className={classes.fullWidth} index={CropTabs.rotate} value={activeTab}>
              <View justifyContent="space-between" gap={Spacings.s6} dataTestId={testIds.rotateView}>
                {ROTATE_OPTIONS.map(({ Icon, action, name }) => (
                  <Button
                    dataTestId={dynamicTestId('rotate', name)}
                    isDisabled={inProgress}
                    skin={ButtonSkinStatusNegative}
                    variant={ButtonVariants.Text}
                    key={action}
                    onClick={() => handleRotate(action)}
                    centerIcon={Icon}
                    size={ButtonSizeXL}
                  />
                ))}
              </View>
            </TabPanel>
          </View>
        </DynamicBody>
      )}
    />
  );
};

export default memo(CropTool);
